import React, { useState, useEffect } from 'react';
import {
  X, User, Phone, MapPin, Package, CreditCard,
  CheckCircle, Settings, Box, XCircle, Clock, Check,
  Loader2, Image as ImageIcon, AlertCircle, Link as LinkIcon,
  Copy, ExternalLink, MessageCircle, Mail
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl } from '@/config/media';
import { CreatePaymentLinkPane } from '@/components/business/payments/CreatePaymentLinkPane';
import { useToast } from '@/contexts/ToastContext';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';

type OrderStatus = 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'cancelled';

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product_image_url?: string;
}

interface OrderHistory {
  status: OrderStatus;
  created_at: string;
  notes?: string;
}

interface OrderDetail {
  id: string;
  order_number: string;
  customer_id: string;
  total_amount: number;
  order_status: OrderStatus;
  payment_status: string;
  payment_method: string;
  customer_phone?: string;
  customer_email?: string;
  delivery_address?: string;
  notes?: string;
  created_at: string;
  confirmed_at?: string;
  delivered_at?: string;
  customer_order_items: OrderItem[];
  customer_order_history: OrderHistory[];
  profiles?: {
    full_name: string;
    avatar_url?: string;
    phone_number?: string;
    email?: string;
  };
}

const statusConfig: Record<OrderStatus, { label: string; color: string; bgColor: string; Icon: React.ElementType }> = {
  pending: { label: 'Pending', color: '#f39c12', bgColor: 'rgba(243,156,18,0.12)', Icon: Clock },
  confirmed: { label: 'Confirmed', color: '#3498db', bgColor: 'rgba(52,152,219,0.12)', Icon: CheckCircle },
  processing: { label: 'Processing', color: '#9b59b6', bgColor: 'rgba(155,89,182,0.12)', Icon: Settings },
  ready: { label: 'Ready for Pickup/Delivery', color: '#1abc9c', bgColor: 'rgba(26,188,156,0.12)', Icon: Box },
  delivered: { label: 'Delivered', color: '#27ae60', bgColor: 'rgba(39,174,96,0.12)', Icon: Check },
  cancelled: { label: 'Cancelled', color: '#e74c3c', bgColor: 'rgba(231,76,60,0.12)', Icon: XCircle },
};

const statusActions: Record<OrderStatus, { label: string; status: OrderStatus; icon: React.ElementType; color: string }[]> = {
  pending: [
    { label: 'Confirm Order', status: 'confirmed', icon: CheckCircle, color: '#3498db' },
    { label: 'Cancel', status: 'cancelled', icon: XCircle, color: '#e74c3c' },
  ],
  confirmed: [
    { label: 'Start Processing', status: 'processing', icon: Settings, color: '#9b59b6' },
    { label: 'Cancel', status: 'cancelled', icon: XCircle, color: '#e74c3c' },
  ],
  processing: [
    { label: 'Mark Ready', status: 'ready', icon: Box, color: '#1abc9c' },
  ],
  ready: [
    { label: 'Mark Delivered', status: 'delivered', icon: Check, color: '#27ae60' },
  ],
  delivered: [],
  cancelled: [],
};

interface OrderDetailPaneProps {
  businessId: string;
  orderId: string | null;
  onClose: () => void;
  onStatusUpdated: () => void;
}

export const OrderDetailPane: React.FC<OrderDetailPaneProps> = ({ businessId, orderId, onClose, onStatusUpdated }) => {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isCreatingPaymentLink, setIsCreatingPaymentLink] = useState(false);
  const [activePaymentLink, setActivePaymentLink] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{ isOpen: boolean; status: OrderStatus | null }>({ isOpen: false, status: null });
  const toast = useToast();

  useEffect(() => {
    if (orderId) {
      setIsCreatingPaymentLink(false);
      fetchOrderDetails();
    }
  }, [businessId, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const [orderRes, linksRes] = await Promise.all([
        apiClient.get(`/businesses/${businessId}/orders/${orderId}`),
        apiClient.get(`/payment-links`, { params: { businessId, orderId } })
      ]);
      // Fix: The backend returns the order directly, not wrapped in an `order` key.
      setOrder(orderRes.data.order || orderRes.data);
      
      const links = linksRes.data.links || [];
      const pendingLink = links.find((l: any) => l.status === 'pending');
      setActivePaymentLink(pendingLink || null);
    } catch (err: any) {
      console.error('Failed to fetch order details:', err);
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (newStatus: OrderStatus) => {
    setConfirmState({ isOpen: true, status: newStatus });
  };

  const executeStatusUpdate = async () => {
    const newStatus = confirmState.status;
    if (!newStatus) return;

    setUpdating(true);
    try {
      await apiClient.put(`/businesses/${businessId}/orders/${orderId}/status`, {
        status: newStatus,
      });
      toast.success('Status Updated', `Order marked as ${statusConfig[newStatus].label}`);
      setConfirmState({ isOpen: false, status: null });
      fetchOrderDetails();
      onStatusUpdated();
    } catch (err: any) {
      console.error('Failed to update status:', err);
      toast.error('Update Failed', err.response?.data?.error || 'Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  if (!orderId) return null;

  if (isCreatingPaymentLink && order) {
    return (
      <CreatePaymentLinkPane
        businessId={businessId}
        orderId={order.id}
        initialTitle={`Order ${order.order_number}`}
        initialNotes={`Payment for order ${order.order_number}`}
        initialLineItems={order.customer_order_items?.map((item) => ({
          description: item.product_name,
          amount: Number(item.unit_price),
          quantity: item.quantity,
        }))}
        onCancel={() => setIsCreatingPaymentLink(false)}
        onSuccess={() => {
          setIsCreatingPaymentLink(false);
          fetchOrderDetails();
          onStatusUpdated();
        }}
      />
    );
  }

  return (
    <div className="w-full h-full bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-6 py-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Order Details</h2>
        <button onClick={onClose} className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-full transition-colors text-light-subtle dark:text-dark-subtle">
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <Loader2 size={40} className="animate-spin text-primary" />
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Loading order details...</p>
            </div>
          ) : error || !order ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
              <AlertCircle size={48} className="text-red-500" />
              <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Order Not Found</h2>
              <p className="text-light-subtle dark:text-dark-subtle">{error || 'The requested order could not be found.'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Top Banner */}
              <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm flex items-center justify-between">
                <div>
                  <p className="font-bold text-lg text-primary">{order.order_number}</p>
                  <p className="text-xs text-light-subtle dark:text-dark-subtle">
                    {new Date(order.created_at).toLocaleString()}
                  </p>
                </div>
                <span
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                  style={{ backgroundColor: statusConfig[order.order_status].bgColor, color: statusConfig[order.order_status].color }}
                >
                  {React.createElement(statusConfig[order.order_status].Icon, { size: 16 })}
                  {statusConfig[order.order_status].label}
                </span>
              </div>

              {/* Customer Information */}
              <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Customer Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                      <User size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-light-text dark:text-dark-text">
                        {order.profiles?.full_name || 'Customer'}
                      </p>
                      <p className="text-xs text-light-subtle dark:text-dark-subtle">Customer Name</p>
                    </div>
                  </div>
                  {(order.customer_phone || order.profiles?.phone_number) && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                          <Phone size={18} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-light-text dark:text-dark-text">{order.customer_phone || order.profiles?.phone_number}</p>
                          <p className="text-xs text-light-subtle dark:text-dark-subtle">Phone Number</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a 
                          href={`tel:${order.customer_phone || order.profiles?.phone_number}`}
                          className="p-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-full text-light-text dark:text-dark-text hover:text-primary dark:hover:text-primary transition shadow-sm"
                          title="Call Customer"
                        >
                          <Phone size={16} />
                        </a>
                        <a 
                          href={`https://wa.me/${(order.customer_phone || order.profiles?.phone_number)?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hello ${order.profiles?.full_name || 'there'}, this is regarding your order ${order.order_number} on Majengo Suites.`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-full text-light-text dark:text-dark-text hover:text-green-500 dark:hover:text-green-500 transition shadow-sm"
                          title="WhatsApp Customer"
                        >
                          <MessageCircle size={16} />
                        </a>
                      </div>
                    </div>
                  )}
                  {/* Email row if available */}
                  {(order.customer_email || order.profiles?.email) && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                          <Mail size={18} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-light-text dark:text-dark-text truncate">{order.customer_email || order.profiles?.email}</p>
                          <p className="text-xs text-light-subtle dark:text-dark-subtle">Email Address</p>
                        </div>
                      </div>
                      <a 
                        href={`mailto:${order.customer_email || order.profiles?.email}?subject=${encodeURIComponent(`Order ${order.order_number}`)}&body=${encodeURIComponent(`Hello ${order.profiles?.full_name || 'there'},\n\nThis is regarding your order ${order.order_number}...\n\n`)}`}
                        className="p-2 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-full text-light-text dark:text-dark-text hover:text-primary dark:hover:text-primary transition shadow-sm"
                        title="Email Customer"
                      >
                        <Mail size={16} />
                      </a>
                    </div>
                  )}
                  {order.delivery_address && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary/10">
                        <MapPin size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-light-text dark:text-dark-text">{order.delivery_address}</p>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Delivery Address</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Order Items</h3>
                <div className="space-y-3">
                  {order.customer_order_items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-3 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl">
                      {item.product_image_url ? (
                        <img
                          src={getImageUrl(item.product_image_url)}
                          alt={item.product_name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">
                          <ImageIcon size={24} className="text-light-subtle dark:text-dark-subtle" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate text-light-text dark:text-dark-text">{item.product_name}</p>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm text-primary">
                          KES {Number(item.total_price).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Order Summary</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-light-subtle dark:text-dark-subtle">Payment Method</span>
                    <span className="font-semibold text-sm text-light-text dark:text-dark-text">
                      {order.payment_method?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-light-subtle dark:text-dark-subtle">Payment Status</span>
                    <span
                      className="font-semibold text-sm"
                      style={{
                        color: order.payment_status === 'paid' ? '#27ae60' :
                               order.payment_status === 'partially_paid' ? '#f39c12' : '#e74c3c'
                      }}
                    >
                      {order.payment_status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="border-t border-light-border dark:border-dark-border pt-3 flex items-center justify-between">
                    <span className="font-semibold text-light-text dark:text-dark-text">Total Amount</span>
                    <span className="font-bold text-xl text-primary">
                      KES {Number(order.total_amount).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order History */}
              {order.customer_order_history && order.customer_order_history.length > 0 && (
                <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Order History</h3>
                  <div className="space-y-4">
                    {order.customer_order_history.map((history, index) => {
                      const historyStatus = statusConfig[history.status];
                      const HistoryIcon = historyStatus?.Icon || Clock;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: historyStatus?.bgColor }}
                          >
                            <HistoryIcon size={14} style={{ color: historyStatus?.color }} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-light-text dark:text-dark-text">
                              {historyStatus?.label || history.status}
                            </p>
                            <p className="text-xs text-light-subtle dark:text-dark-subtle">
                              {new Date(history.created_at).toLocaleString()}
                            </p>
                            {history.notes && (
                              <p className="text-xs mt-1 italic text-light-subtle dark:text-dark-subtle">{history.notes}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {statusActions[order.order_status]?.length > 0 && (
                <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Update Status</h3>
                  <div className="flex flex-wrap gap-3">
                    {statusActions[order.order_status].map((action) => (
                      <button
                        key={action.status}
                        onClick={() => handleStatusClick(action.status)}
                        disabled={updating}
                        className="flex items-center justify-center flex-1 min-w-[140px] gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: action.color, color: '#fff' }}
                      >
                        {updating ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <action.icon size={16} />
                        )}
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Link Action */}
              {order.payment_status !== 'paid' && (
                <div className="pt-4">
                  {activePaymentLink ? (
                    <div className="bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-light-text dark:text-dark-text">Active Payment Link</span>
                        <span className="bg-primary/10 text-primary text-[10px] font-bold uppercase px-2 py-1 rounded-full">Pending</span>
                      </div>
                      <p className="text-xs text-light-subtle dark:text-dark-subtle mb-4">A payment link has already been generated for this order.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(activePaymentLink.shareUrl);
                            toast.success('Copied', 'Payment link copied to clipboard');
                          }}
                          className="flex-1 flex items-center justify-center gap-2 bg-white dark:bg-dark-card border border-light-border dark:border-dark-border py-2.5 rounded-lg text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition shadow-sm"
                        >
                          <Copy size={16} /> Copy URL
                        </button>
                        <a
                          href={activePaymentLink.shareUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2.5 bg-white dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg text-light-subtle dark:text-dark-subtle hover:text-primary dark:hover:text-primary transition shadow-sm flex items-center justify-center"
                          title="Open Link"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsCreatingPaymentLink(true)}
                      className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded-xl font-bold transition-all shadow-sm"
                    >
                      <LinkIcon size={18} />
                      Generate Payment Link
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Global Alert Modal */}
        {confirmState.status && (
          <ConfirmationModal
            isOpen={confirmState.isOpen}
            onClose={() => setConfirmState({ isOpen: false, status: null })}
            onConfirm={executeStatusUpdate}
            title="Update Status"
            message={`Are you sure you want to mark this order as ${statusConfig[confirmState.status].label}?`}
            confirmText="Yes, Update"
            cancelText="Cancel"
            type="info"
            loading={updating}
          />
        )}
    </div>
  );
};